import { useContext, useEffect, useMemo, useState } from 'react';
import * as z from 'zod';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	useNavigate,
	UseNavigateResult,
	useParams,
	useSearch,
} from '@tanstack/react-router';
import { gql, GraphQLClient } from 'graphql-request';

import {
	GET_ISSUE_BY_ID_QUERY,
	clone,
	Issue,
	isNumeric,
	USER_ROLE,
	ISSUE_LANG_KEY,
	Style,
	LangEnum,
} from '@/constants';
import config from '@/config';

import { useIssueNotFound } from '@hooks/use-issue';
import { AppContext } from '@/App.context';
import { useServiceWorker } from '@/hooks/use-service-worker';
import { QueryRequest } from '@/services/query-client/QueryClient';
import {
	AUTH_KEY,
	getHTTPHeaders,
	GRAPHQL_URL,
	isAccessTokenExpired,
	isRefreshTokenExpired,
	logoutUserJwt,
	REFRESH_TOKEN_KEY,
} from '../home/auth/Authentication.hooks';
import { refreshAccessToken } from '@/templates/basic/auth/Auth.hooks';
import { buildStyles } from '@/components/article/StyleBuilder';
import { Styles } from '@/components/article/Styles';

export const ArticlesSortKeys = z
	.enum(['RELEASE_DATE', 'CREATED', 'UPDATED', 'INDEX'])
	.optional();

export const issueSearchSchema = z.object({
	lang: LangEnum,
	limit: z.number().optional().catch(undefined),
	sortKey: ArticlesSortKeys.catch(undefined),
	reverse: z.boolean().optional(),
});

export type IssueSearch = z.infer<typeof issueSearchSchema>;

/**
 * React hook that load data from navigation
 *
 */
export function useLoad(): NavigationResponse {
	const { id, articleId } = useParams({ strict: false });
	const issueId = id;
	const [currentArticle, setCurrentArticle] = useState<any>(null);
	const { issue, isLoading, error } = useIssue(`${issueId}`);

	useEffect(() => {
		if (!issue) {
			return;
		}
		if (!issue.articles) {
			return;
		}
		let art = issue.articles[0];
		for (const article of issue.articles) {
			if (article.id === articleId || article.slug === articleId) {
				art = article;
				break;
			}
		}
		setCurrentArticle(art);
	}, [issue]);

	return {
		issue,
		currentArticle,
		setCurrentArticle,
		isLoading,
		error,
	};
}

/**
 * React hook that returns the details of the issue
 *
 * @param id - Props that gets the issue by it's id
 *
 */
export function useIssue(id: string): GetIssueResponse {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const search = useSearch({ strict: false });

	const queryKey = [
		'issue',
		id,
		{
			limit: search?.limit,
			sortKey: search?.sortKey,
			reverse: search?.reverse,
		},
	];
	const { queryRequest, onlineStatus: isOnline } = useContext(AppContext);
	const [issue, setIssue] = useState<Issue | null | undefined>();
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const cacheTime = isAdmin
		? 0
		: 1000 * 60 * (config.app?.cache?.ttl?.getIssue || 60);

	const { isPending, error, data } = useQuery<IssueQuery | null>({
		queryKey,
		networkMode: 'always',
		queryFn: async () => {
			return await getIssue({
				id,
				queryClient,
				queryKey,
				queryRequest,
				isOnline,
				navigate,
				limit: search?.limit,
				sortKey: search?.sortKey,
				reverse: search?.reverse,
			});
		},
		staleTime: isOnline ? cacheTime : Infinity,
		gcTime: isOnline ? cacheTime : Infinity,
		retry: isOnline ? 3 : 0,
	});

	useIssueNotFound(issue);

	useEffect(() => {
		if (!data) return;

		setIssue(clone(data.issue));
	}, [data]);

	if (issue && issue.styles) {
		issue.styles = buildStyles(issue.styles);
	}

	return {
		isLoading: isPending,
		error: error ? new Error(error.message) : null,
		issue,
	};
}

async function getIssue({
	id,
	queryClient,
	queryKey,
	queryRequest,
	isOnline,
	navigate,
	limit,
	sortKey,
	reverse,
}: {
	id: string | number;
	queryClient: QueryClient;
	queryKey: Array<any>;
	queryRequest: QueryRequest;
	isOnline: boolean;
	navigate: UseNavigateResult<string>;
	limit?: number;
	reverse?: boolean;
	sortKey?: string;
}): Promise<IssueQuery> {
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const cached: IssueQuery | undefined = queryClient.getQueryData(queryKey);
	// The app is offline
	if (!isOnline) {
		// If i don't have the data in the cache redirect to the homescreen
		if (!cached) {
			navigate({
				to: '/',
			});
			return { issue: null };
		}

		// If i have the data return it
		return cached;
	}

	const isAuthenticated = localStorage.getItem(AUTH_KEY) === 'true';
	let headers = getHTTPHeaders(false);
	let client = new GraphQLClient(GRAPHQL_URL, { headers });

	// The user is not authenticated so i won't request access token
	if (!isAuthenticated) {
		try {
			return queryRequest.queryWithClient(client, issueQueryDocument, {
				id,
				isSlug: !isNumeric(id),
				limit,
				sortKey,
				reverse,
			});
		} catch (e) {
			console.error(e);
			if (cached) return cached;
		}
	}

	if (isAdmin) {
		headers = getHTTPHeaders();
		client = new GraphQLClient(GRAPHQL_URL, { headers });
		return queryRequest.queryWithClient(client, issueQueryDocument, {
			id,
			isSlug: !isNumeric(id),
			limit,
			sortKey,
			reverse,
		});
	}

	if (
		config?.app?.auth?.enabled &&
		(config?.app?.auth?.type === 'jwt' ||
			config?.app?.auth?.type === 'cognito')
	) {
		// I always need the refresh token, if it's missing logout
		const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
		if (!refreshToken) {
			logoutUserJwt();
			return { issue: null };
		}

		// The user is authenticated and the user is online
		const refreshExpired = isRefreshTokenExpired();
		if (refreshExpired) {
			logoutUserJwt();
			return { issue: null };
		}

		let accessExpired = isAccessTokenExpired();
		if (accessExpired) {
			for (let i = 0; i < 3; i++) {
				try {
					await refreshAccessToken(refreshToken);
					accessExpired = false;
					break;
				} catch (e: any) {
					console.error(e);
					continue;
				}
			}
		}

		if (accessExpired) {
			if (cached) return cached;
			navigate({
				to: '/',
			});
			return { issue: null };
		}

		headers = getHTTPHeaders();
		client = new GraphQLClient(GRAPHQL_URL, { headers });
	}

	return queryRequest.queryWithClient(client, issueQueryDocument, {
		id,
		isSlug: !isNumeric(id),
		limit,
		sortKey,
		reverse,
	});
}

export interface IssueQuery {
	issue: Issue | null;
}

interface GetIssueResponse {
	isLoading: boolean;
	issue?: Issue | null;
	error?: Error | null;
}

interface NavigationResponse extends GetIssueResponse {
	currentArticle: any | null | undefined;
	setCurrentArticle: (article: any) => void;
}

export function useStylesMap(issue?: Issue | null): Map<string, Style> | null {
	return useMemo(() => {
		if (!issue) return null;
		const { styles } = issue;
		if (!styles) return new Map();

		return new Styles(issue.styles).styles;
	}, [issue]);
}

/**
 * React hook that load data from navigation
 *
 */
export function useTitle(args: TitleArgs) {
	const { article, lang } = args;

	useEffect(() => {
		if (!article) {
			return;
		}
		if (!lang) {
			return;
		}

		const { name, description } = article;

		if (name[lang]) {
			let title = fixToUtf8(name[lang])
			document.title = title;
		}

		if (description) {
			const metaHeader = document.querySelector(
				'meta[name="description"]',
			);
			if (metaHeader) {
				metaHeader.setAttribute('content', description);
			}
		}
	}, [article]);
}

/**
 * Safely converts and repairs any broken, scrambled, or entity-encoded 
 * dynamic text into clean UTF-8 Arabic text.
 * @param {string} text - The raw dynamic text string.
 * @returns {string} - Clean, properly encoded string.
 */
function fixToUtf8(text: string) {
    if (!text || typeof text !== 'string') return '';

    // Step 1: Decode HTML numerical entities (e.g., &#1593;&#1606;)
    if (text.includes('&')) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        text = doc.body.textContent || doc.documentElement.textContent || text;
    }

    // Step 2: Fix Mojibake/Scrambled text (e.g., "ÚäæÇä" or "Ø¹Ù†ÙˆØ§Ù†")
    // This safely un-mangles bytes forced into Windows-1252 / ISO-8859-1 back to UTF-8
    try {
        const latinBytes = new Uint8Array(text.split('').map(char => char.charCodeAt(0)));
        const decodedText = new TextDecoder('utf-8', { fatal: true }).decode(latinBytes);
        
        // If the output doesn't contain fallback question mark blocks, return the healed string
        if (!decodedText.includes('')) {
            text = decodedText;
        }
    } catch (e) {
        // If it throws an error, the text wasn't encoded as standard Mojibake. 
        // We catch it and proceed with the text as-is.
    }

    return text;
}


interface TitleArgs {
	article: any | null | undefined;
	lang: string;
}

/**
 * React hook that control font
 *
 */
export function useFont(): FontResponse {
	const [fontSize, setFontSize] = useState(
		localStorage.getItem('font-size')
			? parseFloat(`${localStorage.getItem('font-size')}`)
			: 62.5,
	);
	useEffect(() => {
		localStorage.setItem('font-size', `${fontSize}`);
	}, [fontSize]);
	return {
		fontSize,
		setFontSize,
	};
}

interface FontResponse {
	fontSize: number;
	setFontSize: (size: number) => void;
}

/**
 * React hook that runs when an article change
 *
 */
export function useArticle({ issue, article }: ArticleArgs) {
	const navigate = useNavigate();
	useEffect(() => {
		if (!issue) {
			return;
		}
		if (!article) {
			return;
		}

		let hash: undefined | string;

		if (article.hash) {
			hash = article.hash;
		} else if (window.location.hash) {
			hash = window.location.hash;
		}

		navigate({
			replace: true,
			to: '/issues/$id/$articleId',
			params: {
				id: issue.slug || issue.id,
				articleId: article.slug || article.id,
			},
			search: (prevSearch: IssueSearch) => ({ ...prevSearch }),
			hash,
		});

		scrollTop(article.id);
	}, [issue, article]);
	useAutoPlayVideos({ article });
}

interface ArticleArgs {
	issue?: any | null | undefined;
	article: any | null;
}

function useAutoPlayVideos({ article }: ArticleArgs) {
	useEffect(() => {
		if (!article) {
			return;
		}
		const articleNode = document.getElementById(`article-${article.id}`);
		if (!articleNode) {
			return;
		}

		/*Check for video elements*/
		const videoNodes = articleNode.querySelectorAll('video');
		for (const node of videoNodes) {
			if (node.hasAttribute('autoplay')) {
				node.play();
			}
		}
	}, [article]);
}

/**
 * React hook that download images in the background
 *
 */
export function useDownloadAssets(args: DownloadImagesArgs) {
	const { isReady, sendMessage } = useServiceWorker();
	const { issue } = args;
	useEffect(() => {
		if (!isReady) return; // Service worker is not ready
		if (!issue) return; // Issue is not set
		if (!issue.articles) return; // Issue do not have any articles

		const images = [
			...issue.articles.reduce(reduceArticles, new Set<string>()),
		];

		sendMessage({
			type: 'DOWNLOAD_BACKGROUND_IMAGES',
			payload: {
				images,
				IssueID: issue.id,
			},
		});
	}, [issue, isReady]);
}

interface DownloadImagesArgs {
	issue: any | null;
}

/**
 * React hook that handles the article language
 *
 */

export function useArticleLang({
	article,
	isLoading,
	languages,
	setLanguage,
}: ArticleLangArgs): void {
	const { lang } = useContext(AppContext);
	const navigate = useNavigate();
	const search: IssueSearch = useSearch({ strict: false });
	useEffect(() => {
		if (!article || isLoading || !languages.size) return;
		const issueLang = getIssueLang(lang);
		if (search?.lang) {
			if (languages.has(search.lang)) {
				localStorage.setItem(ISSUE_LANG_KEY, search.lang);
				setLanguage(search.lang);
				return;
			}

			// If the lang is passed but is not available i remove it

			navigate({
				to: '.',
				search: (prevSearch: IssueSearch) => {
					delete prevSearch.lang;
					return prevSearch;
				},
			});
			setLanguage(issueLang);
			return;
		}
		setLanguage(issueLang);
	}, [article, search, isLoading, languages]);
}

interface ArticleLangArgs {
	article: any;
	isLoading: boolean;
	languages: Set<string>;
	setLanguage: React.Dispatch<React.SetStateAction<string>>;
}

function getIssueLang(lang: string): string {
	if (!localStorage.getItem(ISSUE_LANG_KEY)) {
		localStorage.setItem(ISSUE_LANG_KEY, lang);
	}

	return `${localStorage.getItem(ISSUE_LANG_KEY)}`;
}

function reduceArticles(acc: Set<string>, article: any) {
	if (!article.components) {
		article.components = null;
		return acc;
	}
	return article.components.reduce(reduceComponents, acc);
}

function reduceComponents(acc: Set<string>, component: any) {
	const { components, imageurl } = component;
	if (components) {
		return components.reduce(reduceComponents, acc);
	}
	if (imageurl) {
		acc.add(imageurl);
	}
	return acc;
}

/*function reduceVideos(acc: Set<string>, article: any) {
	return article.components.reduce(reduceVideo, acc);
}

function reduceVideo(acc: Set<string>, component: any) {
	const { components, moviepath } = component;
	if (components) {
		return components.reduce(reduceComponents, acc);
	}

	if (moviepath) {
		acc.add(moviepath);
	}
	return acc;
}*/

export const issueQueryDocument = gql`
	query GetIssue(
		$id: ID!
		$isSlug: Boolean!
		$sortKey: ArticleSortKeys
		$limit: Int
		$reverse: Boolean
	) {
		issue(id: $id, isSlug: $isSlug) {
			id
			IssueID
			name
			description
			textDirection
			features
			pages
			cover
			externalUrl
			price
			currency
			slug
			data
			updated
			created
			isPurchased
			releaseDate
			styles {
				id
				name
				description
				properties
				supportedDevices
				type
				parent
				tablet
				desktop
				created
				lastModified
			}
			articles(sortKey: $sortKey, reverse: $reverse, limit: $limit) {
				id
				ArticleID
				thumbnail
				role
				author
				style
				name
				slug
				features
				keywords
				channel
				audios {
					id
					url
					lang
				}
				styleProperties
				index
				description
				pages
				components
				style
				updated
				created
				isFeatured
				replica {
					name
					page
					image
					hotspots {
						url
						area {
							x
							y
							width
							height
						}
					}
				}
			}
		}
	}
`;

export const GET_ISSUE_BY_ID = gql`
	${GET_ISSUE_BY_ID_QUERY}
`;

export const GET_ISSUE_BY_SLUG = gql`
	query Issue($slug: ID!) {
		issue(id: $slug, isSlug: true) {
			id
			IssueID
			name
			description
			textDirection
			pages
			cover
			externalUrl
			price
			currency
			slug
			data
			updated
			created
			isPurchased
			releaseDate
			styles {
				id
				name
				description
				properties
				supportedDevices
				type
				parent
				tablet
				desktop
				created
				lastModified
			}
			articles {
				id
				ArticleID
				thumbnail
				role
				style
				author
				name
				slug
				audios {
					id
					url
					lang
				}
				keywords
				channel
				styleProperties
				index
				description
				pages
				components
				style
				updated
				created
				isFeatured
				replica {
					name
					page
					image
					hotspots {
						url
						area {
							x
							y
							width
							height
						}
					}
				}
			}
		}
	}
`;

// TODO Revert to support features
export const HACK_GET_ISSUE_BY_SLUG = gql`
	query Issue($slug: ID!) {
		issue(id: $slug, isSlug: true) {
			id
			name
			description
			textDirection
			pages
			cover
			externalUrl
			price
			currency
			slug
			features
			data
			updated
			isFree
			created
			isPurchased
			releaseDate
			articles {
				id
				ArticleID
				thumbnail
				role
				style
				name
				slug
				audios {
					id
					url
					lang
				}
				keywords
				features
				channel
				styleProperties
				index
				description
				pages
				components
				style
				updated
				created
				isFeatured
				replica {
					name
					page
					image
					hotspots {
						url
						area {
							x
							y
							width
							height
						}
					}
				}
			}
		}
	}
`;

function scrollTop(articleId: string) {
	const el = document.getElementById(`article-${articleId}`);
	if (!el) {
		return;
	}

	const options: any = {
		top: 0,
		left: 0,
		behavior: 'instant',
	};

	el.scroll(options);
}
